import React, {useState, useRef, useEffect} from 'react';
import s from './Venues.module.scss';
import {ReactComponent as Star} from 'images/new-icons/yellow-star.svg';
import {ReactComponent as HalfStar} from 'images/new-icons/half-yellow-star.svg';
import {ReactComponent as GreyStar} from 'images/new-icons/grey-star.svg';
import {ReactComponent as House} from 'images/new-icons/house.svg';
import {ReactComponent as Salon} from 'images/new-icons/shop.svg';
import {ReactComponent as RiyalIcon} from 'images/new-icons/riyal.svg';
import {useNavigate} from 'react-router-dom';
import VenuesMap from './VenuesMap';
import clsx from 'clsx';
import {supabase} from '../../../sdk/api';
import {useTranslation} from 'react-i18next';

interface Salon {
    id: string;
    name: string;
    salon: {
        id: string;
        first_name: string;
        avatar: string;
    };
    image: string;
    price: number; // NEW - changed from min_price
    service_location?: string; // NEW - for home icon
    // min_price: number; // OLD
}

const getTransformValue = () => {
    const width = window.innerWidth;

    if (width <= 360) return 'calc(85% - 13%)';
    if (width <= 375) return 'calc(81% - 13%)';
    if (width <= 390) return 'calc(87% - 13%)';
    if (width <= 414) return 'calc(90% - 13%)';
    if (width <= 430) return 'calc(90% - 13%)';
    return 'calc(86% - 13%)';
};

const Popular = ({
                     salons,
                     distances,
                     setDistances,
                     selectedMenuCategory,
                     productId,
                     brand,
                     minPrice,
                     maxPrice,
                     cityId,
                     proximity,
                     isExpanded,
                     setIsExpanded,
                     tab,
                     minRating,
                     debouncedSearchText
                 }: any) => {
    const {t} = useTranslation() as any;
    const navigate = useNavigate();
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const [selectedSalon, setSelectedSalon] = useState<Salon | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startTransform = useRef(0);
    const [salonReviews, setSalonReviews] = useState<Record<string, any[]>>({});
    const touchStartY = useRef(0);

    const handleTouchStart = (e: TouchEvent) => {
        const container = containerRef.current;
        if (!container) return;

        if (container.scrollTop > 0 && isExpanded) {
            isDragging.current = false;
            return;
        }

        isDragging.current = true;
        startY.current = e.touches[0].clientY;

        const transform = new WebKitCSSMatrix(window.getComputedStyle(container).transform);
        startTransform.current = transform.m42;
    };

    const handleTouchMove = (e: TouchEvent) => {
        const container = containerRef.current;
        if (!isDragging.current || !container) return;

        requestAnimationFrame(() => {
            const deltaY = e.touches[0].clientY - startY.current;

            if (deltaY < 0 && isExpanded) {
                isDragging.current = false;
                return;
            }

            e.preventDefault();

            const newTransform = startTransform.current + deltaY;
            const maxTransform = 0;
            const minTransform = container.offsetHeight * 0.65;

            const boundedTransform = Math.min(Math.max(newTransform, -maxTransform), minTransform);
            container.style.transform = `translateY(${boundedTransform}px) translateZ(0)`;
        });
    };

    const handleTouchEnd = () => {
        if (!isDragging.current || !containerRef.current) return;

        const transform = new WebKitCSSMatrix(window.getComputedStyle(containerRef.current).transform);
        const currentY = transform.m42;

        const snapThreshold = containerRef.current.offsetHeight * (isExpanded ? 0.001 : 0.8);
        const shouldExpand = currentY < snapThreshold;

        isDragging.current = false;

        containerRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

        requestAnimationFrame(() => {
            if (containerRef.current) {
                containerRef.current.style.transform = shouldExpand
                    ? 'translateY(0) translateZ(0)'
                    : `translateY(${getTransformValue()}) translateZ(0)`;
            }
        });

        setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.style.transition = '';
            }
        }, 300);

        if (shouldExpand !== isExpanded) {
            setIsExpanded(shouldExpand);
            if (!shouldExpand && containerRef.current) {
                containerRef.current.scrollTop = 0;
            }
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('touchstart', handleTouchStart);
            container.addEventListener('touchmove', handleTouchMove);
            container.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            if (container) {
                container.removeEventListener('touchstart', handleTouchStart);
                container.removeEventListener('touchmove', handleTouchMove);
                container.removeEventListener('touchend', handleTouchEnd);
            }
        };
    }, [isExpanded]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.transform = isExpanded
                ? 'translateY(0)'
                : `translateY(${getTransformValue()})`;
        }
    }, [isExpanded]);

    useEffect(() => {
        const fetchAllReviews = async () => {
            const reviewsPromises = salons?.map(async item => {
                const {data: reviews} = await supabase
                    .from('reviews')
                    .select('*')
                    .eq('salon', item?.salon?.id);
                return {id: item?.salon?.id, reviews};
            });

            const allReviews = await Promise.all(reviewsPromises || []);
            const reviewsMap = allReviews.reduce((acc, {id, reviews}) => {
                acc[id] = reviews;
                return acc;
            }, {});

            setSalonReviews(reviewsMap);
        };

        fetchAllReviews();
    }, [salons]);

    const getAverageRating = (salonId: string) => {
        const reviews = salonReviews[salonId] || [];
        if (!reviews.length) return 0;
        const total = reviews.reduce((acc, review) => acc + review.rank, 0);
        return (total / reviews.length).toFixed(1);
    };

    const renderStars = (rating: number) => {
        const stars: JSX.Element[] = [];
        let remainingRank = rating;

        for (let i = 0; i < 5; i++) {
            if (remainingRank >= 1) {
                stars.push(<Star key={i} className={s.star_filled}/>);
                remainingRank -= 1;
            } else if (remainingRank > 0) {
                stars.push(<HalfStar key={i} className={s.star_half}/>);
                remainingRank = 0;
            } else {
                stars.push(<GreyStar key={i} className={s.star_empty}/>);
            }
        }

        return stars;
    };

    const sortSalons = salons => {
        if (!salons) return [];

        let sortedSalons = [...salons];

        switch (tab) {
            case 'all':
                return sortedSalons;

            case 'top_rated':
                return sortedSalons.sort((a, b) => {
                    const aRating = Number(getAverageRating(a?.salon?.id)) || 0;
                    const bRating = Number(getAverageRating(b?.salon?.id)) || 0;
                    return bRating - aRating;
                });

            case 'good_price':
                // NEW - using price field instead of min_price
                return sortedSalons.sort((a, b) => {
                    const aPrice = a?.price || 0;
                    const bPrice = b?.price || 0;
                    return aPrice - bPrice;
                });
            // OLD
            // return sortedSalons.sort((a, b) => {
            //   const aPrice = a?.min_price || 0;
            //   const bPrice = b?.min_price || 0;
            //   return aPrice - bPrice;
            // });

            case 'best_location':
                return sortedSalons.sort((a, b) => {
                    const aDistance = distances?.find(d => a?.salon?.id === d?.id)?.distance || Infinity;
                    const bDistance = distances?.find(d => b?.salon?.id === d?.id)?.distance || Infinity;
                    return aDistance - bDistance;
                });

            default:
                return sortedSalons;
        }
    };

    const updatedSalons = sortSalons(salons)
        ?.map(item => ({
            ...item,
            distance: distances?.find(d => item?.salon?.id === d?.id)?.distance,
        }))
        ?.filter(item => (proximity ? item.distance && Math.floor(item.distance) <= proximity : true));

    const handleMarkerClick = (salon: Salon) => {
        setSelectedSalon(salon);
    };

    const clearSelectedSalon = () => {
        setSelectedSalon(null);
    };

    // Add resize listener
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current && !isExpanded) {
                containerRef.current.style.transform = `translateY(${getTransformValue()})`;
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isExpanded]);

    return (
        <>
            <VenuesMap
                data={salons}
                setDistances={setDistances}
                distances={distances}
                selectedMenuCategory={selectedMenuCategory}
                productId={productId}
                brand={brand}
                minPrice={minPrice}
                maxPrice={maxPrice}
                cityId={cityId}
                isMapExpanded={isMapExpanded}
                setIsMapExpanded={setIsMapExpanded}
                onMarkerClick={handleMarkerClick}
                clearSelectedSalon={clearSelectedSalon}
                minRating={minRating}
                debouncedSearchText={debouncedSearchText}
            />
            <div
                ref={containerRef}
                className={clsx(s.container, isExpanded && s.expanded)}
                style={{display: isMapExpanded ? 'none' : 'flex'}}
            >
                {selectedSalon ? (
                    <div
                        className={s.venues_module}
                        onClick={() => navigate(`/salon-details/${selectedSalon.salon.id}`)}
                    >
                        <div className={s.venues_module_name}>{selectedSalon.name}</div>
                    </div>
                ) : updatedSalons?.length ? (
                    updatedSalons.map(item => (
                        <div
                            className={s.venues_card}
                            key={item?.id}
                            onClick={() => navigate(`/salon-details/${item?.id}`)}
                            onTouchStart={e => {
                                e.stopPropagation();
                                touchStartY.current = e.touches[0].clientY;
                            }}
                            onTouchEnd={e => {
                                const endY = e.changedTouches[0].clientY;
                                if (Math.abs(endY - touchStartY.current) < 5) {
                                    e.preventDefault();
                                    navigate(`/salon-details/${item?.id}`);
                                }
                            }}
                        >
                            <div className={s.venues_card_header}>
                                <div className={s.venues_card_header_left}>
                                    <img
                                        className={s.venues_card_header_left_img}
                                        src={item?.salon?.avatar}
                                        alt={item?.salon?.name || 'Salon'}
                                    />
                                    <div className={s.venues_card_header_left_text}>{item?.salon?.name}</div>
                                </div>
                                {/* NEW - Show actual price */}
                                <div className={s.venues_card_header_right} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <RiyalIcon style={{ width: '14px', height: '14px' }} />
                                    {item?.price || ''}
                                </div>
                                {/* OLD */}
                                {/* <div className={s.venues_card_header_right}>SAR</div> */}
                            </div>

                            <div className={s.venues_card_name}>
                                {item?.name}
                                <span className={s.venues_card_location_badge}>
                                    {t(item?.service_location)}
                                    {item?.service_location === 'At Home' && (
                                        <House className={s.home_icon}/>
                                    )}
                                    {item?.service_location === 'In Salon' && (
                                        <Salon className={s.home_icon}/>
                                    )}
                                    {item?.service_location === 'Both' && (
                                        <>
                                            <House className={s.home_icon}/>
                                            <Salon className={s.home_icon}/>
                                        </>
                                    )}
                                </span>
                            </div>

                            {/* OLD */}
                            {/* <div className={s.venues_card_name}>{item?.name}</div> */}

                            <div className={s.venues_card_reviews}>
                                <div className={s.venues_card_reviews_left}>
                                    <div className={s.venues_card_reviews_left_stars}>
                                        {salonReviews[item?.salon?.id]?.length
                                            ? renderStars(Math.round(Number(getAverageRating(item?.salon?.id))))
                                            : ''}
                                    </div>
                                    <div className={s.venues_card_reviews_left_text}>
                                        {salonReviews[item?.salon?.id]?.length
                                            ? salonReviews[item?.salon?.id]?.length + t('reviews')
                                            : t('No reviews')}
                                    </div>
                                </div>
                                <div className={s.venues_card_reviews_right}>
                                    {item?.distance ? item.distance.toFixed(0) + t("km from you") : t('Loading...')}
                                </div>
                            </div>
                            <img className={s.venues_card_image} src={item?.image} alt={item?.name || 'Venue'}/>
                        </div>
                    ))
                ) : (
                    <div>No data available</div>
                )}
            </div>
        </>
    );
};

export default Popular;